
import PostsListCardItem from '@/components/post/PostsListCardItem.vue';
import ManagePlatformPostsModal from '@/components/post/ManagePlatformPostsModal.vue';
import { Post, Bot, Chain, SupportedPlatform } from 'ignite360-core';
import { Component, Prop, Vue } from 'vue-property-decorator';

/** True when the project has a linked Instagram Business account with API tokens. */
function isInstagramAccountConnectedForProject(bot: Bot | null | undefined): boolean {
  if (!bot?.platformData?.instagram) {
    return false;
  }
  const ig = bot.platformData.instagram;
  return !!(
    ig.pageId &&
    ig.pageAccessToken &&
    ig.pageName &&
    ig.businessAccountId &&
    ig.userAccessToken
  );
}

/** True when Facebook posting is enabled and page credentials are stored. */
function isFacebookPostingConnectedForProject(bot: Bot | null | undefined): boolean {
  if (!bot?.platformData?.facebookMessenger) {
    return false;
  }
  const fb = bot.platformData.facebookMessenger;
  return !!(fb.pageId && fb.pageAccessToken && fb.enablePosting);
}

/** True when LinkedIn posting is enabled for the connected page or profile. */
function isLinkedInPostingConnectedForProject(bot: Bot | null | undefined): boolean {
  if (!bot?.platformData?.linkedIn) {
    return false;
  }
  const li = bot.platformData.linkedIn;
  return !!(li._id && li.type && li.enablePosting);
}

/** True when Google Business posting is enabled for the connected location. */
function isGoogleBusinessPostingConnectedForProject(bot: Bot | null | undefined): boolean {
  if (!bot?.platformData?.googleBusiness) {
    return false;
  }
  const gb = bot.platformData.googleBusiness;
  return !!(gb.accountId && gb.locationId && gb.enablePosting && !gb.isExpired);
}

@Component({
  name: 'posts-list-card',
  components: { PostsListCardItem, ManagePlatformPostsModal },
})
export default class PostsListCard extends Vue {
  @Prop({ required: true, type: Array })
  posts!: Post[];

  @Prop({ required: false, type: Object })
  readonly chain!: Chain;

  @Prop({ required: false, type: Object })
  readonly bot!: Bot;

  showManageModal = false;

  /** Bots used to resolve connected social posting accounts. */
  get botsInScope(): Bot[] {
    if (this.bot) {
      return [this.bot];
    }
    return this.chain?.bots || [];
  }

  get instagramLinkBot(): Bot | null {
    return this.botsInScope.find((b) => isInstagramAccountConnectedForProject(b)) || null;
  }

  get facebookLinkBot(): Bot | null {
    return this.botsInScope.find((b) => isFacebookPostingConnectedForProject(b)) || null;
  }

  get linkedInLinkBot(): Bot | null {
    return this.botsInScope.find((b) => isLinkedInPostingConnectedForProject(b)) || null;
  }

  get googleBusinessLinkBot(): Bot | null {
    return this.botsInScope.find((b) => isGoogleBusinessPostingConnectedForProject(b)) || null;
  }

  get manageBotIdsByPlatform(): Partial<Record<SupportedPlatform, string>> {
    return {
      [SupportedPlatform.Instagram]: this.instagramLinkBot?.id || '',
      [SupportedPlatform.FacebookMessenger]: this.facebookLinkBot?.id || '',
      [SupportedPlatform.LinkedIn]: this.linkedInLinkBot?.id || '',
      [SupportedPlatform.GoogleBusiness]: this.googleBusinessLinkBot?.id || '',
    };
  }

  get manageablePlatforms(): SupportedPlatform[] {
    const rows: SupportedPlatform[] = [];
    if (this.instagramLinkBot) {
      rows.push(SupportedPlatform.Instagram);
    }
    if (this.facebookLinkBot) {
      rows.push(SupportedPlatform.FacebookMessenger);
    }
    if (this.linkedInLinkBot) {
      rows.push(SupportedPlatform.LinkedIn);
    }
    if (this.googleBusinessLinkBot) {
      rows.push(SupportedPlatform.GoogleBusiness);
    }
    return rows;
  }

  sortPosts = (a: Post, b: Post) => {
    const lastUpdatedA = new Date(a.lastUpdatedAt!).getTime();
    const lastUpdatedB = new Date(b.lastUpdatedAt!).getTime();
    if (lastUpdatedA > lastUpdatedB) {
      return -1;
    } else if (lastUpdatedA < lastUpdatedB) {
      return 1;
    }
    return 0;
  };
  filterPosts = (post: Post, filterBy: string) =>
    post.title.toLowerCase().includes(filterBy) ||
    post.content.toLowerCase().includes(filterBy);
  getPostKey = (post: Post) => post.id;
}
